/**
 * The app's HTTP client: the canonical vendored factory, configured for this
 * console's two bases.
 *
 *  - saas-auth (the bootstrap `authOrigin`) — identity + the cookie-bearing
 *    auth surface on an absolute, cross-origin base. Credentialed; CSRF
 *    double-submit applies.
 *  - the tmm backend (same-origin at `/api`) — operations. Bearer-only,
 *    UNcredentialed, no CSRF.
 * These bases must not converge: this host proxies `/api/*` to the tmm backend, so a
 * relative auth path would silently reach the wrong service.
 *
 * Tokens are in memory only. Interceptor logic lives in
 * `./httpClientFactory` (vendored, never edited) — this module supplies config.
 */
import { createApiClient } from './httpClientFactory';
import { apiBase } from '@/config/platform';
import { getBootstrap } from '@/config/bootstrap';
import {
  getAccessToken,
  getCsrfToken,
  setAccessToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';
import { generateRequestId } from '@/utils/uuid';

/**
 * The tmm backend operational surface. `resolveBaseUrl` and `sendCredentials` MUST
 * agree on this prefix: a path routed to the tmm backend but not recognised by the
 * credential predicate would be sent credentialed and fail CORS preflight. One
 * constant so they cannot drift.
 *
 * ASSUMPTION: the tmm backend versions every route under `/v1/` (its api-docs
 * contract mirror); no admin route exists yet, so `/v1/admin/` is the
 * assumption. Confirm against the tmm backend's contract when the first
 * operational page is specced; this constant is the single change point, and
 * the predicate tests below pin it.
 */
export const TMM_API_PREFIX = '/v1/admin/';

/**
 * Exact auth-origin paths under CSRF double-submit. `/api/auth/session/exchange`
 * is deliberately ABSENT: it is pre-session and not CSRF-gated (saas-auth
 * SecurityConfig / SASATH-0218).
 */
const CSRF_PROTECTED_EXACT: ReadonlySet<string> = new Set([
  '/api/auth/refresh',
  '/api/auth/logout',
]);
const CSRF_PROTECTED_PREFIXES: readonly string[] = ['/api/account/', '/api/product-admin/'];

export function isTmmApiPath(path: string): boolean {
  return path.startsWith(TMM_API_PREFIX);
}

export function resolveBaseUrl(path: string): string {
  return isTmmApiPath(path) ? apiBase() : getBootstrap().authOrigin;
}

/**
 * Per-base credential posture. Cookies travel to the auth origin only — the
 * refresh cookie is HttpOnly and ambient there. The tmm backend authenticates by
 * bearer token alone, has no use for cookies, and does not emit
 * `Access-Control-Allow-Credentials`; sending credentials to it makes the
 * browser demand that header and fails every preflight.
 */
export function sendCredentials(path: string): boolean {
  return !isTmmApiPath(path);
}

export function isBearerPath(path: string): boolean {
  return (
    isTmmApiPath(path) || path.startsWith('/api/account/') || path.startsWith('/api/product-admin/')
  );
}

/** CSRF applies to the auth origin ONLY. The tmm backend writes are bearer-only. */
export function isCsrfProtected(path: string): boolean {
  if (isTmmApiPath(path)) return false;
  if (CSRF_PROTECTED_EXACT.has(path)) return true;
  return CSRF_PROTECTED_PREFIXES.some((p) => path.startsWith(p));
}

export const api = createApiClient({
  resolveBaseUrl,
  sendCredentials,
  tokens: {
    getAccess: getAccessToken,
    setAccess: setAccessToken,
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: isBearerPath },
  csrf: {
    protected: (path) => isCsrfProtected(path),
    // saas-auth's surface is same-origin-cookie readable (`journix_csrf`) with a
    // GET /api/auth/csrf fallback — cookie-echo, header + body echo.
    mode: () => 'cookie-echo',
  },
  refresh: { path: '/api/auth/refresh' },
  onSessionExpired: signOut,
  generateRequestId,
});
